import { isValidElement, useMemo, type ReactElement, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'
import { MermaidDiagram } from './MermaidDiagram'
import { resolveDocLink, type Doc } from '../lib/docs'
import { useAuth } from '../context/AuthContext'

// The operator view strips engineering-flavored content from the doc body.
// Sections cut:
//   - "## Implementation notes" and everything that follows it
//   - Inline schema asides usually live inside Implementation notes already,
//     so removing the section catches the bulk in one shot.
// This keeps the same set of docs visible in the sidebar — operators can
// still navigate to any doc — but the body is the operational steps + UI
// description, not the endpoint paths and migrations.
function stripImplementationNotes(body: string): string {
  const marker = body.search(/(^|\n)##\s+Implementation notes\b/i)
  if (marker < 0) return body
  // Cut from the newline before the heading (or 0 if it's at the very top).
  const newlineBefore = body[marker] === '\n' ? marker : marker - 1
  return body.slice(0, Math.max(0, newlineBefore)).replace(/\s+$/, '') + '\n'
}

const STATUS_STYLE: Record<string, string> = {
  stable: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40',
  draft: 'bg-amber-900/40 text-amber-300 border-amber-700/40',
  'pending-integration': 'bg-orange-900/40 text-orange-300 border-orange-700/40',
}

const SECTION_LABEL: Record<string, string> = {
  customer: 'Customer App',
  driver: 'Ambassador App',
  admin: 'Admin Portal',
  _shared: 'Cross-App Policy',
  engineering: 'Engineering',
  operations: 'Operations Runbook',
  integrations: 'Third-Party Integration',
  brand: 'Brand & Style',
}

export function DocViewer({ doc }: { doc: Doc }) {
  const navigate = useNavigate()
  const { can } = useAuth()
  const isEngineerView = can(['super_admin'])
  // Operator view trims the engineering-flavored Implementation notes section.
  // Memoised on doc.slug because the strip is pure; recomputing per render
  // would be wasted work on long docs.
  const body = useMemo(
    () => (isEngineerView ? doc.body : stripImplementationNotes(doc.body)),
    [doc.slug, doc.body, isEngineerView],
  )

  const components: Components = {
    h1: ({ children }) => <h1 className="text-3xl font-bold text-white mt-10 mb-4 first:mt-0">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-semibold text-white mt-10 mb-3 pb-2 border-b border-[#2a2a2a]">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-semibold text-white mt-6 mb-2">{children}</h3>,
    h4: ({ children }) => <h4 className="text-base font-semibold text-white mt-4 mb-2">{children}</h4>,
    p: ({ children }) => <p className="text-gray-300 leading-relaxed mb-4">{children}</p>,
    ul: ({ children }) => <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-1.5">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 text-gray-300 space-y-1.5">{children}</ol>,
    li: ({ children }) => <li className="text-gray-300 leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
    em: ({ children }) => <em className="text-gray-200 italic">{children}</em>,
    hr: () => <hr className="border-[#2a2a2a] my-8" />,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-[#C41E3A] bg-[#C41E3A]/5 pl-4 py-2 my-4 text-gray-300 italic">
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto my-4">
        <table className="w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-[#1a1a1a]">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr className="border-b border-[#2a2a2a]">{children}</tr>,
    th: ({ children }) => <th className="text-left px-3 py-2 border border-[#2a2a2a] text-gray-200 font-semibold">{children}</th>,
    td: ({ children }) => <td className="px-3 py-2 border border-[#2a2a2a] text-gray-300 align-top">{children}</td>,

    a: ({ href, children }) => {
      if (!href) return <>{children}</>
      // Internal KB link?
      if (href.endsWith('.md') || href.includes('.md#')) {
        const route = resolveDocLink(doc, href)
        if (route) {
          return (
            <a
              href={route}
              onClick={e => { e.preventDefault(); navigate(route) }}
              className="text-[#E8556F] hover:text-[#C41E3A] underline decoration-dotted underline-offset-2"
            >
              {children}
            </a>
          )
        }
        // .md reference outside the KB (SNAGS.md, memory files, etc.) — render
        // muted so it's clear it's not clickable without breaking the layout.
        return (
          <span
            className="text-gray-400 underline decoration-dotted underline-offset-2 cursor-help"
            title={`Out-of-KB reference: ${href}`}
          >
            {children}
          </span>
        )
      }
      // External
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#E8556F] hover:text-[#C41E3A] underline decoration-dotted underline-offset-2"
        >
          {children}
        </a>
      )
    },

    // react-markdown v9: `code` fires for both inline and fenced code. Only
    // fenced blocks carry a `language-*` className.
    code: ({ className, children }) => {
      if (className) {
        // Fenced block — `pre` wraps it and handles the container styling.
        return <code className={className}>{children}</code>
      }
      return (
        <code className="bg-white/10 text-[#fbb] px-1.5 py-0.5 rounded text-[0.875em] font-mono">
          {children}
        </code>
      )
    },
    // Intercept Mermaid fences here (not in `code`) so the diagram <div>
    // doesn't end up inside a <pre>, which would be invalid HTML.
    pre: ({ children }) => {
      const child = Array.isArray(children) ? children[0] : children
      if (isValidElement(child)) {
        const props = (child as ReactElement<{ className?: string; children?: ReactNode }>).props
        if (props.className === 'language-mermaid') {
          return <MermaidDiagram code={String(props.children).replace(/\n$/, '')} />
        }
      }
      return (
        <pre className="my-4 p-4 bg-[#0c0c0c] border border-[#2a2a2a] rounded text-sm text-gray-200 overflow-x-auto">
          {children}
        </pre>
      )
    },
  }

  return (
    <article className="max-w-3xl mx-auto py-10 px-8">
      <header className="mb-10 pb-6 border-b border-[#2a2a2a]">
        <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-widest mb-3">
          <span>{SECTION_LABEL[doc.section] ?? doc.section}</span>
          {doc.frontmatter.audience && (
            <>
              <span>·</span>
              <span>{doc.frontmatter.audience}</span>
            </>
          )}
        </div>
        <h1 className="text-3xl font-bold text-white mb-4 leading-tight">{doc.frontmatter.title}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] px-2 py-0.5 rounded border ${STATUS_STYLE[doc.frontmatter.status] ?? STATUS_STYLE.draft}`}>
            {doc.frontmatter.status}
          </span>
          {doc.frontmatter.updated && (
            <span className="text-[11px] text-gray-500">Updated {doc.frontmatter.updated}</span>
          )}
          {doc.frontmatter.tags.length > 0 && doc.frontmatter.tags.map(tag => (
            <span key={tag} className="text-[11px] text-gray-400 bg-white/5 px-2 py-0.5 rounded">{tag}</span>
          ))}
        </div>
      </header>

      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {body}
      </ReactMarkdown>

      {!isEngineerView && doc.body !== body && (
        <p className="mt-10 pt-6 border-t border-[#2a2a2a] text-xs text-gray-500">
          Engineering details for this doc (endpoints, schema, file paths) are visible
          to super-admin users.
        </p>
      )}
    </article>
  )
}
